/**
 * Price formatting utilities for adaptive chart display.
 * Calculates appropriate decimal precision, scaling parameters, and thresholds
 * based on the token pair's price magnitude.
 */

/**
 * Price configuration for a specific price magnitude.
 * All values are calculated relative to the reference price.
 */
export interface PriceConfig {
    // Display precision
    /** Number of decimals for Y-axis tick labels */
    tickDecimals: number;
    /** Number of decimals for cursor/current price label */
    cursorDecimals: number;
    /** Number of decimals for general price display */
    displayDecimals: number;

    // Scaling parameters
    /** Minimum Y-axis range (prevents over-zooming) */
    minSpread: number;

    // Animation thresholds (scaled to price)
    /** Threshold for cursor twitch animation */
    twitchThreshold: number;
    /** Threshold for price flash effect */
    flashThreshold: number;

    // Trend detection thresholds
    /** Price difference threshold to consider bullish */
    bullishThreshold: number;
    /** Price difference threshold to consider bearish (negative) */
    bearishThreshold: number;

    // Formatting functions
    /** Format value for Y-axis tick labels */
    formatTick: (value: number) => string;
    /** Format value for cursor label */
    formatCursor: (value: number) => string;
    /** Format value for general display */
    formatDisplay: (value: number) => string;
}

/**
 * Price magnitude tier configuration.
 * Defines how prices at different magnitudes should be formatted and scaled.
 */
interface MagnitudeTier {
    minPrice: number;
    tickDecimals: number;
    cursorDecimals: number;
    displayDecimals: number;
    minSpreadMultiplier: number; // Multiplied by price to get minSpread
    thresholdMultiplier: number; // Multiplied by price to get thresholds
}

/**
 * Magnitude tiers from highest to lowest price.
 * Each tier defines formatting rules for a price range.
 */
const MAGNITUDE_TIERS: MagnitudeTier[] = [
    {
        minPrice: 10000,
        tickDecimals: 0,
        cursorDecimals: 1,
        displayDecimals: 1,
        minSpreadMultiplier: 0.002, // e.g., 90000 * 0.002 = 180 (wider Y-range for high volatility)
        thresholdMultiplier: 0.0002, // Higher threshold to reduce twitch/flash sensitivity
    },
    {
        minPrice: 1000,
        tickDecimals: 1,
        cursorDecimals: 2,
        displayDecimals: 2,
        minSpreadMultiplier: 0.003, // e.g., 2500 * 0.003 = 7.5 (wider Y-range for ETH etc.)
        thresholdMultiplier: 0.001, // Higher threshold to reduce animation sensitivity
    },
    {
        minPrice: 100,
        tickDecimals: 2,
        cursorDecimals: 2,
        displayDecimals: 2,
        minSpreadMultiplier: 0.005, // e.g., 500 * 0.005 = 2.5
        thresholdMultiplier: 0.001,
    },
    {
        minPrice: 1,
        tickDecimals: 2,
        cursorDecimals: 3,
        displayDecimals: 2,
        minSpreadMultiplier: 0.02, // e.g., 50 * 0.02 = 1
        thresholdMultiplier: 0.005,
    },
    {
        minPrice: 0.1,
        tickDecimals: 3,
        cursorDecimals: 4,
        displayDecimals: 4,
        minSpreadMultiplier: 0.015, // e.g., 0.5 * 0.015 = 0.0075 (tighter zoom)
        thresholdMultiplier: 0.004, // More responsive animations
    },
    {
        minPrice: 0.01,
        tickDecimals: 4,
        cursorDecimals: 5,
        displayDecimals: 5,
        minSpreadMultiplier: 0.03, // e.g., 0.09 * 0.025 = 0.00225 (tighter zoom)
        thresholdMultiplier: 0.006, // More responsive animations
    },
    {
        minPrice: 0.001,
        tickDecimals: 5,
        cursorDecimals: 6,
        displayDecimals: 5,
        minSpreadMultiplier: 0.15, // e.g., 0.005 * 0.15 = 0.00075
        thresholdMultiplier: 0.02,
    },
    {
        minPrice: 0, // Catch-all for very small prices
        tickDecimals: 6,
        cursorDecimals: 7,
        displayDecimals: 6,
        minSpreadMultiplier: 0.2,
        thresholdMultiplier: 0.05,
    },
];

/**
 * Finds the appropriate magnitude tier for a given price.
 */
const getTierForPrice = (price: number): MagnitudeTier => {
    const absPrice = Math.abs(price);
    for (const tier of MAGNITUDE_TIERS) {
        if (absPrice >= tier.minPrice) {
            return tier;
        }
    }
    // Return the last tier (smallest prices) as fallback
    return MAGNITUDE_TIERS[MAGNITUDE_TIERS.length - 1];
};

/**
 * Cache for formatter functions by decimal count.
 * Prevents creating new function instances on every getPriceConfig call.
 */
const formatterCache = new Map<number, (value: number) => string>();

/**
 * Creates a number formatter with thousand separators and fixed decimals.
 * For large numbers: "90,000.1"
 * For small numbers: "0.091410"
 */
const createFormatter = (decimals: number): ((value: number) => string) => {
    return (value: number): string => {
        // Use toFixed for precision, then add thousand separators for the integer part
        const fixed = value.toFixed(decimals);

        // Split into integer and decimal parts
        const [intPart, decPart] = fixed.split('.');

        // Add thousand separators to integer part
        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        // Return with or without decimal part
        return decPart ? `${formattedInt}.${decPart}` : formattedInt;
    };
};

/**
 * Gets or creates a cached formatter for the specified decimal count.
 * Reuses existing formatters to avoid creating new function instances.
 */
const getOrCreateFormatter = (
    decimals: number,
): ((value: number) => string) => {
    const cached = formatterCache.get(decimals);
    if (cached) {
        return cached;
    }
    const formatter = createFormatter(decimals);
    formatterCache.set(decimals, formatter);
    return formatter;
};

/**
 * Calculates the order of magnitude (power of 10) for a price.
 * Used for detecting significant price changes that require reconfiguration.
 */
export const getOrderOfMagnitude = (price: number): number => {
    if (price === 0) return 0;
    return Math.floor(Math.log10(Math.abs(price)));
};

/**
 * Generates a complete PriceConfig for a given reference price.
 *
 * @param referencePrice - The reference price to base configuration on
 * @returns Complete price configuration with formatting functions and thresholds
 *
 * @example
 * // For BTC at ~90,000
 * const btcConfig = getPriceConfig(90000);
 * btcConfig.formatTick(90123.456); // "90,123"
 * btcConfig.formatCursor(90123.456); // "90,123.5"
 *
 * @example
 * // For CRO at ~0.09
 * const croConfig = getPriceConfig(0.09141);
 * croConfig.formatTick(0.091234); // "0.0912"
 * croConfig.formatCursor(0.091234); // "0.09123"
 */
export const getPriceConfig = (referencePrice: number): PriceConfig => {
    const absPrice = Math.abs(referencePrice) || 1; // Prevent division by zero
    const tier = getTierForPrice(absPrice);

    // Calculate scaled thresholds
    const baseThreshold = absPrice * tier.thresholdMultiplier;
    const minSpread = Math.max(
        absPrice * tier.minSpreadMultiplier,
        Math.pow(10, -tier.tickDecimals), // Minimum based on display precision
    );

    return {
        // Display precision
        tickDecimals: tier.tickDecimals,
        cursorDecimals: tier.cursorDecimals,
        displayDecimals: tier.displayDecimals,

        // Scaling parameters
        minSpread,

        // Animation thresholds
        twitchThreshold: baseThreshold,
        flashThreshold: baseThreshold * 5, // Flash needs larger change

        // Trend detection thresholds
        bullishThreshold: baseThreshold * 4,
        bearishThreshold: -(baseThreshold * 4),

        // Formatting functions (cached by decimal count)
        formatTick: getOrCreateFormatter(tier.tickDecimals),
        formatCursor: getOrCreateFormatter(tier.cursorDecimals),
        formatDisplay: getOrCreateFormatter(tier.displayDecimals),
    };
};

/**
 * Checks if the price has changed enough to warrant a new PriceConfig.
 * Returns true if the order of magnitude has changed by at least 1.
 *
 * @param oldPrice - Previous reference price
 * @param newPrice - New reference price
 * @returns Whether the config should be recalculated
 */
export const shouldRecalculateConfig = (
    oldPrice: number,
    newPrice: number,
): boolean => {
    const oldMagnitude = getOrderOfMagnitude(oldPrice);
    const newMagnitude = getOrderOfMagnitude(newPrice);
    return Math.abs(oldMagnitude - newMagnitude) >= 1;
};

/**
 * Default price config for fallback scenarios.
 * Based on a ~$100 reference price.
 */
export const DEFAULT_PRICE_CONFIG: PriceConfig = getPriceConfig(100);
